/**
 * Host authentication helpers for localStorage-based token management.
 *
 * Two types of tokens:
 * 1. hostGrant - Issued after entering host access code, allows creating rooms (12h TTL)
 * 2. hostToken - Issued on room creation, required for all host actions on that room
 *
 * Roles:
 * - "admin": Full access (host + editor + theme)
 * - "host": Limited access (host + editor only)
 */

const HOST_TOKEN_KEY = "host-token";
const HOST_ROOM_CODE_KEY = "host-room-code";
const HOST_GRANT_KEY = "host-grant";
const HOST_GRANT_EXPIRES_KEY = "host-grant-expires";
const HOST_GRANT_ROLE_KEY = "host-grant-role";

//----------------------------------------------------------------------------------------------------------------------
// Role constants (must match backend)
//----------------------------------------------------------------------------------------------------------------------

export const ROLES = {
    ADMIN: "admin",
    HOST: "host"
};

//----------------------------------------------------------------------------------------------------------------------
// Host Session management (for active quiz hosting)
//----------------------------------------------------------------------------------------------------------------------

/**
 * Check if a valid host session exists (roomCode + hostToken in localStorage)
 */
export function hasHostSession() {
    return (
        localStorage.getItem(HOST_TOKEN_KEY) !== null &&
        localStorage.getItem(HOST_ROOM_CODE_KEY) !== null
    );
}

/**
 * Get the stored hostToken
 */
export function getHostToken() {
    return localStorage.getItem(HOST_TOKEN_KEY);
}

/**
 * Get the stored host room code
 */
export function getHostRoomCode() {
    return localStorage.getItem(HOST_ROOM_CODE_KEY);
}

/**
 * Save host session (called on successful room creation)
 */
export function saveHostSession(roomCode, hostToken) {
    localStorage.setItem(HOST_ROOM_CODE_KEY, roomCode);
    localStorage.setItem(HOST_TOKEN_KEY, hostToken);
}

/**
 * Clear host session (called when host ends quiz or token is invalid)
 */
export function clearHostSession() {
    localStorage.removeItem(HOST_TOKEN_KEY);
    localStorage.removeItem(HOST_ROOM_CODE_KEY);
}

//----------------------------------------------------------------------------------------------------------------------
// Host Grant management (for host access code authentication)
//----------------------------------------------------------------------------------------------------------------------

/**
 * Save host grant with role (called after successful host access code authentication)
 */
export function setHostGrant(token, expiresAt, role) {
    localStorage.setItem(HOST_GRANT_KEY, token);
    localStorage.setItem(HOST_GRANT_EXPIRES_KEY, String(expiresAt));
    localStorage.setItem(HOST_GRANT_ROLE_KEY, role || ROLES.HOST);
}

/**
 * Get the stored host grant token
 */
export function getHostGrant() {
    return localStorage.getItem(HOST_GRANT_KEY);
}

/**
 * Get the stored role for the current grant
 * Returns "admin", "host", or null if no valid grant
 */
export function getHostRole() {
    if (!hasValidHostGrant()) {
        return null;
    }
    return localStorage.getItem(HOST_GRANT_ROLE_KEY) || ROLES.HOST;
}

/**
 * Clear host grant and role
 */
export function clearHostGrant() {
    localStorage.removeItem(HOST_GRANT_KEY);
    localStorage.removeItem(HOST_GRANT_EXPIRES_KEY);
    localStorage.removeItem(HOST_GRANT_ROLE_KEY);
}

/**
 * Check if a valid (non-expired) host grant exists
 */
export function hasValidHostGrant() {
    const grant = localStorage.getItem(HOST_GRANT_KEY);
    const expiresAt = localStorage.getItem(HOST_GRANT_EXPIRES_KEY);

    if (!grant || !expiresAt) {
        return false;
    }

    // Check if expired
    const expiresAtNum = parseInt(expiresAt, 10);
    if (isNaN(expiresAtNum) || Date.now() > expiresAtNum) {
        // Expired - clean up
        clearHostGrant();
        return false;
    }

    return true;
}

//----------------------------------------------------------------------------------------------------------------------
// Role-based access helpers
//----------------------------------------------------------------------------------------------------------------------

/**
 * Check if user has at least the required role.
 * Role hierarchy: admin > host
 *
 * @param {string} requiredRole - "host" or "admin"
 * @returns {boolean} true if user has sufficient permissions
 */
export function hasRole(requiredRole) {
    const currentRole = getHostRole();

    if (!currentRole) {
        return false;
    }

    // Admin has all permissions
    if (currentRole === ROLES.ADMIN) {
        return true;
    }

    // Host can only access host-level features
    if (currentRole === ROLES.HOST && requiredRole === ROLES.HOST) {
        return true;
    }

    return false;
}

/**
 * Check if user has host-level access (host or admin)
 */
export function hasHostAccess() {
    return hasValidHostGrant() && hasRole(ROLES.HOST);
}

/**
 * Check if user has admin-level access
 */
export function hasAdminAccess() {
    return hasValidHostGrant() && hasRole(ROLES.ADMIN);
}
